import React, { useMemo, useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileSpreadsheet, Loader2, TrendingUp, TrendingDown, Target, Info, Download, CalendarIcon } from 'lucide-react';
import { Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { useClients, useLoans, useDashboardStats, exportToCSV, useBranchPerformance, useOfficerLeaderboard, useProfitLoss, useCashFlow } from '@/hooks/use-api';
import { useBranchStore } from '@/lib/store';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const COLORS = ['#0c3f6e', '#0e8ce9', '#38a9f8', '#bae0fd'];

export function ReportsPage() {
  const { data: clientsData, isLoading: clientsLoading } = useClients();
  const { data: loansData } = useLoans();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const selectedBranchId = useBranchStore(s => s.selectedBranchId);
  const [dateRange, setDateRange] = useState();
  
  const startDate = dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined;
  const endDate = dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined;

  const { data: branchPerfData, isLoading: branchPerfLoading } = useBranchPerformance(startDate, endDate);
  const { data: leaderboardData, isLoading: leaderboardLoading } = useOfficerLeaderboard(startDate, endDate);
  const { data: pnlData, isLoading: pnlLoading } = useProfitLoss(startDate, endDate);
  const { data: cashFlowData, isLoading: cashFlowLoading } = useCashFlow(startDate, endDate);

  const handleExport = () => {
    if (!clientsData?.items?.length) {
      toast.error("No data available to export");
      return;
    }
    const exportData = clientsData.items.map(c => ({
      ...c,
      branch: selectedBranchId === 'all' ? 'Consolidated' : c.branchId
    }));
    exportToCSV(exportData, `perfect-match-financials-${new Date().toISOString().split('T')[0]}.csv`);
    toast.success("CSV export complete");
  };

  const incomeData = useMemo(() => [
    { name: 'Interest', value: stats?.totalInterestCollected || 0 },
    { name: 'Penalties', value: stats?.totalPenaltiesCollected || 0 },
    { name: 'Susu Savings', value: stats?.totalCollections || 0 },
  ].filter(i => i.value > 0), [stats]);

  const netPosition = useMemo(() => {
    if (!stats) return 0;
    return (stats.totalCollections || 0) + (stats.totalInterestCollected || 0) + (stats.totalPenaltiesCollected || 0) - (stats.totalDisbursements || 0);
  }, [stats]);

  const completedLoans = useMemo(() =>
    loansData?.items.filter(l => l.status === 'Completed') || []
  , [loansData]);
  
  const branchPerfChartData = useMemo(() => 
    branchPerfData?.items.map(b => ({ name: b.branch, amount: b.amount })) || [],
    [branchPerfData]
  );

  const leaderboardChartData = useMemo(() => 
    leaderboardData?.items.map(o => ({ name: o.name, collections: o.collections })) || [],
    [leaderboardData]
  );

  const handleClearDateRange = () => {
    setDateRange(undefined);
  };

  return (
    <AppLayout container className="bg-accent/20 scrollbar-custom">
      <div className="space-y-8 animate-fade-in">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Financial Performance Suite</h1>
            <p className="text-muted-foreground mt-1 font-medium">Comprehensive P&L and portfolio exit analytics.</p>
          </div>
          <div className="flex items-center gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-border rounded-xl text-muted-foreground font-bold h-10 px-3">
                  <CalendarIcon className="h-4 w-4 mr-2" />
                  {startDate ? `${format(dateRange?.from || new Date(), 'MMM dd')} - ${dateRange?.to ? format(dateRange.to, 'MMM dd') : format(dateRange?.from || new Date(), 'MMM dd')}` : 'Date Range'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-2xl border-border" align="end">
                <DateRangePicker
                  date={dateRange}
                  onDateChange={setDateRange}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
            <Button variant="outline" onClick={handleExport} disabled={clientsLoading} className="border-border rounded-xl text-muted-foreground font-bold">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Master Export
            </Button>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-4">
          <Card className="bg-primary text-primary-foreground border-t-4 border-muted shadow-xl rounded-2xl overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-[10px] uppercase font-bold tracking-widest text-primary-foreground">Net Portfolio Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-black">GHS {netPosition.toLocaleString()}</div>
               <div className="flex items-center gap-1 mt-2 text-xs text-primary-foreground font-bold">
                 <TrendingUp className="h-3 w-3" /> System Verified
               </div>
            </CardContent>
          </Card>
          <Card className="shadow-soft border-none bg-card rounded-2xl">
            <CardContent className="p-6">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Interest Yield</p>
              <div className="text-2xl font-black text-foreground mt-1">GHS {(stats?.totalInterestCollected ?? 0).toLocaleString()}</div>
              <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground font-bold uppercase">Accrued Volume</div>
            </CardContent>
          </Card>
          <Card className="shadow-soft border-none bg-card rounded-2xl">
            <CardContent className="p-6">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Disbursements</p>
              <div className="text-2xl font-black text-destructive mt-1">GHS {(stats?.totalDisbursements ?? 0).toLocaleString()}</div>
              <div className="flex items-center gap-1 mt-2 text-[10px] text-destructive font-bold uppercase">
                <TrendingDown className="h-3 w-3" /> Portfolio Outflow
              </div>
            </CardContent>
          </Card>
          <Card className="shadow-soft border-none bg-card rounded-2xl">
            <CardContent className="p-6">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Target Recovery</p>
              <div className="text-2xl font-black text-muted-foreground mt-1">{stats?.defaultRiskRate ? (100 - stats.defaultRiskRate).toFixed(1) : 98}%</div>
              <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground font-bold uppercase">
                <Target className="h-3 w-3 mr-1" /> Efficiency Index
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="flex flex-col h-full border border-border shadow-soft rounded-3xl bg-card overflow-hidden">
            <CardHeader className="bg-muted/50 border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground">Revenue Composition</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 min-h-[400px] p-6">
              {statsLoading ? (
                <div className="h-full w-full flex items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : incomeData.length > 0 ? (
                <div className="h-full w-full">
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie data={incomeData} cx="50%" cy="50%" innerRadius={70} outerRadius={120} paddingAngle={5} dataKey="value">
                        {incomeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 20px rgba(12,63,110,0.1)' }}
                      />
                      <Legend verticalAlign="bottom" height={36} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center text-muted-foreground gap-2">
                  <Info className="h-8 w-8 opacity-20" />
                  <p className="text-sm font-black uppercase tracking-widest">No Revenue Data Available</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="h-full border border-border shadow-soft rounded-3xl overflow-hidden bg-card">
            <CardHeader className="bg-muted/50 border-b border-border">
              <CardTitle className="text-xl font-bold text-foreground">Portfolio Exit Tracker</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto scrollbar-custom">
                <Table>
                  <TableHeader className="bg-muted/50">
                    <TableRow className="hover:bg-muted/50 border-border">
                      <TableHead className="pl-6 font-bold text-foreground">Ref ID</TableHead>
                      <TableHead className="font-bold text-foreground">Principal</TableHead>
                      <TableHead className="font-bold text-foreground">Status</TableHead>
                      <TableHead className="pr-6 font-bold text-foreground text-right">Verification</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {completedLoans.map(l => (
                      <TableRow key={l.id} className="border-border hover:bg-muted/50">
                        <TableCell className="pl-6 font-mono text-xs text-muted-foreground">{l.id.slice(0, 8).toUpperCase()}</TableCell>
                        <TableCell className="font-bold text-foreground">GHS {l.amount.toLocaleString()}</TableCell>
                        <TableCell><Badge className="bg-secondary text-secondary-foreground border-none font-bold text-[10px]">CLOSED</Badge></TableCell>
                        <TableCell className="pr-6 text-right">
                          <Badge variant="outline" className="text-muted-foreground border-border bg-muted/50 font-bold text-[10px]">AUDITED</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {completedLoans.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-24">
                          <div className="flex flex-col items-center gap-2">
                             <Target className="h-10 w-10 text-muted-foreground/20" />
                             <p className="text-sm font-bold uppercase tracking-widest text-muted-foreground">No Matured Loans Found</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Profit & Loss Report */}
        <Card className="border border-border shadow-soft rounded-3xl overflow-hidden bg-card">
          <CardHeader className="bg-muted/50 border-b border-border">
            <CardTitle className="text-xl font-bold text-foreground">Profit & Loss Statement</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {pnlLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : pnlData ? (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">Revenue</h4>
                  <div className="space-y-3">
                    {pnlData.revenue.map((item, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-border/50">
                        <span className="font-medium text-foreground">{item.source}</span>
                        <span className="font-black text-emerald-500">GHS {item.amount.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-3 border-t-2 border-border">
                      <span className="font-black text-lg uppercase tracking-tight text-foreground">Total Revenue</span>
                      <span className="font-black text-lg text-emerald-500">GHS {(pnlData.revenue.reduce((s, i) => s + i.amount, 0)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">Expenses</h4>
                  <div className="space-y-3">
                    {pnlData.expenses.map((item, i) => (
                      <div key={i} className="flex justify-between items-center py-2 border-b border-border/50">
                        <span className="font-medium text-foreground">{item.source}</span>
                        <span className="font-black text-red-500">GHS {item.amount.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-3 border-t-2 border-border">
                      <span className="font-black text-lg uppercase tracking-tight text-foreground">Total Expenses</span>
                      <span className="font-black text-lg text-red-500">GHS {(pnlData.expenses.reduce((s, i) => s + i.amount, 0)).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="mt-6 p-4 bg-muted/50 rounded-xl">
                    <div className="flex justify-between items-center">
                      <span className="font-black text-lg uppercase tracking-tight text-foreground">Net Income</span>
                      <span className={`font-black text-2xl ${pnlData.netIncome >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        GHS {pnlData.netIncome.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <Info className="h-8 w-8 opacity-20" />
                <p className="text-sm font-black uppercase tracking-widest">No P&L Data Available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cash Flow Report */}
        <Card className="border border-border shadow-soft rounded-3xl overflow-hidden bg-card">
          <CardHeader className="bg-muted/50 border-b border-border">
            <CardTitle className="text-xl font-bold text-foreground">Cash Flow Statement</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {cashFlowLoading ? (
              <div className="h-64 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : cashFlowData ? (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-widest text-emerald-600 mb-4 flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Operating Activities
                    </h4>
                    <div className="space-y-2">
                      {cashFlowData.operating.map((item, i) => (
                        <div key={i} className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-3">
                          <span className="text-sm font-medium text-foreground">{item.source}</span>
                          <span className="font-black text-emerald-500">GHS {item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-widest text-blue-600 mb-4 flex items-center gap-2">
                      <Target className="h-4 w-4" /> Investing Activities
                    </h4>
                    <div className="space-y-2">
                      {cashFlowData.investing.map((item, i) => (
                        <div key={i} className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-3">
                          <span className="text-sm font-medium text-foreground">{item.source}</span>
                          <span className="font-black text-blue-500">GHS {item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-widest text-purple-600 mb-4 flex items-center gap-2">
                      <Download className="h-4 w-4" /> Financing Activities
                    </h4>
                    <div className="space-y-2">
                      {cashFlowData.financing.map((item, i) => (
                        <div key={i} className="flex justify-between items-center py-2 bg-muted/50 rounded-lg px-3">
                          <span className="text-sm font-medium text-foreground">{item.source}</span>
                          <span className="font-black text-purple-500">GHS {item.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="border-t-2 border-border pt-6">
                  <div className="grid grid-cols-3 gap-6">
                    <div className="bg-muted/30 rounded-xl p-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Opening Balance</p>
                      <p className="text-2xl font-black text-foreground">GHS {cashFlowData.openingBalance.toLocaleString()}</p>
                    </div>
                    <div className="bg-muted/30 rounded-xl p-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Net Cash Flow</p>
                      <p className={`text-2xl font-black ${cashFlowData.netCashFlow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        GHS {cashFlowData.netCashFlow.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-muted/30 rounded-xl p-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Closing Balance</p>
                      <p className="text-2xl font-black text-foreground">GHS {cashFlowData.closingBalance.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <Info className="h-8 w-8 opacity-20" />
                <p className="text-sm font-black uppercase tracking-widest">No Cash Flow Data Available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branch Performance */}
        <Card className="border border-border shadow-soft rounded-3xl overflow-hidden bg-card">
          <CardHeader className="bg-muted/50 border-b border-border">
            <CardTitle className="text-xl font-bold text-foreground">Branch Performance Comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {branchPerfLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : branchPerfChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={branchPerfChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                    />
                    <YAxis tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 20px rgba(12,63,110,0.1)' }}
                      formatter={(value: number) => [`GHS ${value.toLocaleString()}`, 'Total Collections']}
                    />
                    <Bar dataKey="amount" fill="#0c3f6e" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <Info className="h-8 w-8 opacity-20" />
                <p className="text-sm font-black uppercase tracking-widest">No Branch Performance Data</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Officer Leaderboard */}
        <Card className="border border-border shadow-soft rounded-3xl overflow-hidden bg-card">
          <CardHeader className="bg-muted/50 border-b border-border">
            <CardTitle className="text-xl font-bold text-foreground">Officer Leaderboard - Top Collectors</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {leaderboardLoading ? (
              <div className="h-80 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : leaderboardChartData.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={leaderboardChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      width={150}
                    />
                    <RechartsTooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 20px rgba(12,63,110,0.1)' }}
                      formatter={(value: number) => [`GHS ${value.toLocaleString()}`, 'Total Collections']}
                    />
                    <Bar dataKey="collections" fill="#0e8ce9" radius={[0, 8, 8, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex flex-col items-center justify-center text-muted-foreground gap-2">
                <Info className="h-8 w-8 opacity-20" />
                <p className="text-sm font-black uppercase tracking-widest">No Officer Performance Data</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
